import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Check, Crown, Loader2, Minus, Plus, Sparkles } from 'lucide-react';
import { Button } from 'components/ui/button';
import { StatusBadge } from 'components/ui/status-badge';
import { cn } from 'lib/utils';
import type { AiGradeSuggestion, PendingAnswer, PendingAttempt } from 'modules/grading/api';

/** What the desktop split pane and the phone grader share: the queue shape and the scoring pieces. */

export interface QueueItem {
  attemptId: string;
  answerId: string;
  candidateName: string;
  /** False when the attempt carries neither a name nor a blind-grading label. */
  hasName: boolean;
  testId: string;
  testTitle: string;
  questionId: string;
  questionText: string;
  questionType: string;
  maxPoints: number;
  value: unknown;
  aiScore?: number;
  aiConfidence?: 'HIGH' | 'MEDIUM' | 'LOW';
  aiReasoning?: string;
  writingTask?: 'TASK_1' | 'TASK_2';
  aiBandCriteria?: Array<{ key: string; band: number }>;
  speakingRecordings?: PendingAnswer['speakingRecordings'];
  aiAvailable?: boolean;
}

export interface RubricCriterion {
  id: string;
  label: string;
  points: number;
}

export type GradingMode = 'question' | 'respondent';

export interface QueueGroup {
  key: string;
  /** Respondent mode: the respondent's name. */
  name: string;
  /** Question mode: the question's queue number. */
  questionNumber: number;
  items: QueueItem[];
}

// Every type that can land in the manual-grading queue and its i18n label key
// (same keys used by TestTaking/QuestionsManagerPage).
const QUESTION_TYPE_LABEL_KEY: Record<string, string> = {
  MULTIPLE_CHOICE: 'single_choice',
  MULTIPLE_RESPONSE: 'multiple_response',
  TRUE_FALSE: 'true_false',
  SHORT_ANSWER: 'short_answer',
  MATCHING: 'matching',
  FILL_IN_BLANKS: 'fill_in_blanks',
  OPEN_ENDED: 'open_ended',
  REORDER: 'reorder',
  PASSAGE: 'passage',
  SPEAKING: 'exam.speaking.type',
};

export const questionTypeLabelKey = (type: string): string => QUESTION_TYPE_LABEL_KEY[type] || 'question';

export const answerToText = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(answerToText).join('; ');
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

/** The pending queue as one row per answer, optionally scoped to a test. */
export function buildQueueItems(pending: PendingAttempt[], t: TFunction, testId?: string): QueueItem[] {
  const out: QueueItem[] = [];
  for (const att of pending) {
    if (testId && att.test?.id !== testId) continue;
    const label = att.candidate?.name || (att.anonymousLabel != null ? t('grading_respondent_n', { n: att.anonymousLabel }) : '');
    for (const ans of att.answers ?? []) {
      const q = ans.question;
      out.push({
        attemptId: att.id,
        answerId: ans.id,
        candidateName: label || t('respondent'),
        hasName: Boolean(label),
        testId: att.test?.id || '',
        testTitle: att.test?.title || '',
        questionId: q?._id ?? q?.id ?? '',
        questionText: q?.prompt ?? '',
        questionType: q?.type ?? '',
        maxPoints: q?.points ?? 1,
        value: ans.value,
        aiScore: ans.aiScore,
        aiConfidence: ans.aiConfidence,
        aiReasoning: ans.aiReasoning,
        writingTask: q?.writingTask,
        aiBandCriteria: ans.aiBandCriteria,
        speakingRecordings: ans.speakingRecordings,
        aiAvailable: ans.aiAvailable,
      });
    }
  }
  return out;
}

/**
 * Questions numbered 1, 2, 3… in the order the queue meets them. The pending
 * endpoint carries no question order within its test, so this is queue order.
 */
export function questionNumbers(items: QueueItem[]): Map<string, number> {
  const numbers = new Map<string, number>();
  items.forEach((item, index) => {
    const key = item.questionId || `q${index}`;
    if (!numbers.has(key)) numbers.set(key, numbers.size + 1);
  });
  return numbers;
}

export const questionNumberOf = (numbers: Map<string, number>, item: QueueItem, index: number): number =>
  numbers.get(item.questionId || `q${index}`) ?? index + 1;

/** One group per question (question mode) or per respondent's attempt (respondent mode). */
export function groupQueue(items: QueueItem[], mode: GradingMode, numbers: Map<string, number>): QueueGroup[] {
  const groups = new Map<string, QueueGroup>();
  items.forEach((item, index) => {
    const key = mode === 'question' ? item.questionId || `q${index}` : item.attemptId;
    const current = groups.get(key);
    groups.set(key, {
      key,
      name: item.candidateName,
      questionNumber: questionNumberOf(numbers, item, index),
      items: [...(current?.items ?? []), item],
    });
  });
  return Array.from(groups.values());
}

/** "<Name>ning javobi", or the generic label when the attempt has no name. */
export const answerLabel = (item: QueueItem, t: TFunction): string =>
  item.hasName ? t('grading_answer_by', { name: item.candidateName }) : t('candidate_s_answer');

export function GradingStatus({ graded, className }: { graded: boolean; className?: string }) {
  const { t } = useTranslation();
  return graded ? (
    <span className={cn('inline-flex shrink-0 items-center gap-1 text-xs text-success-700 dark:text-success-400', className)}>
      <Check className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
      {t('grading_status_graded')}
    </span>
  ) : (
    <span className={cn('inline-flex shrink-0 items-center gap-1.5 text-xs text-warning-700 dark:text-warning-400', className)}>
      <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-warning-500" />
      {t('grading_status_pending')}
    </span>
  );
}

type Density = 'studio' | 'phone';

interface AiSuggestionBoxProps {
  suggestion: AiGradeSuggestion | null;
  /** The applied score is the AI's. */
  matchesAi: boolean;
  requesting: boolean;
  /** The plan includes AI grading; otherwise the request opens the upgrade dialog. */
  canUseAi: boolean;
  onRequest: () => void;
  onAccept: () => void;
  onAdjust: () => void;
  density?: Density;
}

/** AI is assistive: the grader accepts the suggested score or adjusts from it — never applied on its own. */
export function AiSuggestionBox({
  suggestion,
  matchesAi,
  requesting,
  canUseAi,
  onRequest,
  onAccept,
  onAdjust,
  density = 'studio',
}: AiSuggestionBoxProps) {
  const { t } = useTranslation();
  const size = density === 'phone' ? 'lg' : 'xs';

  if (!suggestion) {
    return (
      <div data-grading-ai className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border px-4 py-3.5">
        <span className="flex min-w-0 items-center gap-2 text-[13.5px] text-muted-foreground">
          <Sparkles className="h-4 w-4 shrink-0" aria-hidden />
          {t('grading_ai_get')}
        </span>
        <Button variant="outline" size={size} className="gap-1.5" onClick={onRequest} disabled={requesting}>
          {requesting ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            !canUseAi && <Crown className="h-3.5 w-3.5 text-warning-600" aria-hidden />
          )}
          {t('ai_suggestion')}
        </Button>
      </div>
    );
  }

  return (
    <div data-grading-ai className="rounded-lg border border-border px-4 py-3.5">
      <div className="flex flex-wrap items-center gap-2.5">
        <Sparkles className="h-4 w-4 text-muted-foreground" aria-hidden />
        <span className="text-[13.5px] font-semibold text-foreground">{t('ai_suggestion')}</span>
        <span className="text-[13.5px] tabular-nums text-foreground">
          {suggestion.suggestedScore} / {suggestion.maxPoints}
        </span>
        {suggestion.confidence && (
          <StatusBadge tone="neutral" dot={false}>
            {t(`confidence_${suggestion.confidence.toLowerCase()}`)}
          </StatusBadge>
        )}
      </div>
      {suggestion.rationale && (
        <p className="mt-2 text-[13.5px] leading-relaxed text-foreground/80">{suggestion.rationale}</p>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="outline" size={size} className="gap-1.5" onClick={onAccept} aria-pressed={matchesAi}>
          {matchesAi && <Check className="h-4 w-4" aria-hidden />}
          {matchesAi ? t('grading_applied') : t('grading_accept_pts', { count: suggestion.suggestedScore })}
        </Button>
        <Button variant="ghost" size={size} onClick={onAdjust}>
          {t('grading_adjust')}
        </Button>
      </div>
    </div>
  );
}

interface ScoreStepperProps {
  value: number;
  max: number;
  onStep: (delta: number) => void;
  density?: Density;
}

/** − value / max +. Phones get 44px targets. */
export function ScoreStepper({ value, max, onStep, density = 'studio' }: ScoreStepperProps) {
  const { t } = useTranslation();
  const button = cn(
    'flex shrink-0 items-center justify-center rounded-md border border-border bg-card text-foreground/80 transition-colors hover:border-foreground/20 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40',
    density === 'phone' ? 'h-11 w-11' : 'h-7 w-7',
  );
  return (
    <div className="flex shrink-0 items-center gap-3">
      <button type="button" onClick={() => onStep(-1)} disabled={value <= 0} aria-label={t('decrease_score')} className={button}>
        <Minus className="h-3.5 w-3.5" aria-hidden />
      </button>
      <span className="w-11 text-center text-[13.5px] font-semibold tabular-nums text-foreground">
        {value} / {max}
      </span>
      <button type="button" onClick={() => onStep(1)} disabled={value >= max} aria-label={t('increase_score')} className={button}>
        <Plus className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  );
}

interface GradingRubricProps {
  /** The test's rubric; without one the answer gets a single overall score. */
  criteria: RubricCriterion[];
  awarded: Record<string, number>;
  maxPoints: number;
  onStep: (id: string, delta: number, max: number) => void;
  density?: Density;
}

/** Rubric rows with steppers; the key "__single" holds the overall score. */
export function GradingRubric({ criteria, awarded, maxPoints, onStep, density = 'studio' }: GradingRubricProps) {
  const { t } = useTranslation();
  const rows =
    criteria.length > 0
      ? criteria.map((c) => ({ id: c.id, label: c.label, max: c.points }))
      : [{ id: '__single', label: t('grading_overall_score'), max: maxPoints }];
  return (
    <section data-grading-rubric>
      <h3 className="text-[13.5px] font-semibold text-foreground">{t('grading_rubric')}</h3>
      <ul className="mt-1.5">
        {rows.map((row) => (
          <li key={row.id} className="flex items-center gap-3 border-t border-border py-2.5">
            <span className="min-w-0 flex-1 text-[13.5px] text-foreground">{row.label}</span>
            <ScoreStepper
              value={awarded[row.id] ?? 0}
              max={row.max}
              onStep={(delta) => onStep(row.id, delta, row.max)}
              density={density}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

interface GraderNoteProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
}

/** Persisted as the answer's manual note on save. */
export function GraderNote({ id, value, onChange, rows = 2 }: GraderNoteProps) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[13.5px] font-semibold text-foreground">
        {t('grading_note_label')}
      </label>
      <textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t('grading_note_placeholder')}
        rows={rows}
        className="w-full resize-y rounded-md border border-input bg-card px-3 py-2.5 text-[13.5px] text-foreground outline-none focus-visible:!outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
      />
    </div>
  );
}
